import { useEffect, useState } from "react";
import { Download, Moon } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";

// Admin › Warehouse › Reports › Nightly inventory detail (BE
// services/report-snapshots.js, migration 238). At UK midnight the job
// runner stores the Inventory Detail export of every warehouse with stock;
// this lists one warehouse's stored files, newest first, each downloadable
// as it was built. Hidden for someone without Inventory access to the
// warehouse (the list answers 403).

export interface NightlySnapshot {
    id: number;
    report_id: string;
    warehouse: string;
    as_of: string;
    status: "building" | "ready" | "failed";
    keep: boolean;
    file_name: string | null;
    byte_size: number | null;
    row_count: number | null;
    started_at: string | null;
    built_at: string | null;
}

const LONDON = "Europe/London";

// The day a midnight file closes: as_of is 00:00 on the next day.
const closedDayOf = (asOf: string) =>
    new Date(Date.parse(asOf) - 12 * 3_600_000).toLocaleDateString("en-GB", { timeZone: LONDON });

const timeOf = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString("en-GB", { timeZone: LONDON, day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const sizeOf = (bytes: number | null) => (bytes == null ? "" : `${(bytes / 1_048_576).toFixed(1)} MB`);

const ReportsNightly = ({ warehouse }: { warehouse: string }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<NightlySnapshot[]>([]);
    const [allowed, setAllowed] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    useEffect(() => {
        if (!warehouse) return;
        let alive = true;
        setLoaded(false);
        (async () => {
            try {
                const res = await apiFetch(`/reports/snapshots?warehouse=${encodeURIComponent(warehouse)}`, {});
                if (res.status === 403) { if (alive) { setAllowed(false); setRows([]); } return; }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (alive) { setAllowed(true); setRows(Array.isArray(data?.snapshots) ? data.snapshots : []); }
            } catch {
                if (alive) toast.error(t("reports_nightly_load_failed"), { toastId: "reports-nightly-load" });
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [warehouse, t]);

    const download = async (row: NightlySnapshot) => {
        setDownloadingId(row.id);
        try {
            const res = await apiFetch(`/reports/snapshots/${row.id}/download`, {});
            if (!res.ok) {
                const data = await res.json().catch(() => null);
                throw new Error(data?.message || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = row.file_name || `inventory-detail-${row.warehouse}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(`${t("reports_download_failed")}${e instanceof Error && e.message ? ` (${e.message})` : ""}`, { toastId: "reports-nightly-download" });
        } finally {
            setDownloadingId(null);
        }
    };

    if (!allowed) return null;

    const statusLabel = (s: NightlySnapshot["status"]) =>
        s === "ready" ? t("reports_nightly_ready") : s === "building" ? t("reports_nightly_building") : t("reports_nightly_failed");

    return (
        <section className="reports-group reports-nightly">
            <h3 className="reports-group-title"><Moon size={14} className="reports-nightly-icon" />{t("reports_nightly_title")}</h3>
            <p className="reports-nightly-hint">{t("reports_nightly_hint")}</p>
            {loaded && rows.length === 0 && <p className="reports-empty">{t("reports_nightly_none", { warehouse })}</p>}
            {rows.length > 0 && (
                <div className="locations-table-wrap">
                    <table className="locations-table reports-nightly-table">
                        <thead>
                            <tr>
                                <th>{t("reports_nightly_col_stock_at")}</th>
                                <th>{t("reports_nightly_col_lines")}</th>
                                <th>{t("reports_nightly_col_size")}</th>
                                <th>{t("reports_nightly_col_built")}</th>
                                <th>{t("reports_nightly_col_status")}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row.id}>
                                    <td>
                                        {t("reports_nightly_end_of", { date: closedDayOf(row.as_of) })}
                                        {row.keep && <span className="reports-nightly-badge">{t("reports_nightly_month_end")}</span>}
                                    </td>
                                    <td>{row.row_count?.toLocaleString("en-GB") ?? ""}</td>
                                    <td>{sizeOf(row.byte_size)}</td>
                                    <td>{timeOf(row.built_at)}</td>
                                    <td className={`reports-nightly-status reports-nightly-status--${row.status}`}>{statusLabel(row.status)}</td>
                                    <td>
                                        {row.status === "ready" && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => download(row)}
                                                disabled={downloadingId !== null}
                                                aria-label={`${t("reports_download")} ${closedDayOf(row.as_of)}`}
                                            >
                                                <Download size={14} />
                                                {downloadingId === row.id ? t("reports_nightly_downloading") : t("reports_download")}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default ReportsNightly;
